import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { StyleClassModule } from 'primeng/styleclass';
import { MenuService } from '@services/MenuBaseService/menu-base.service';
import { Subscription } from 'rxjs';
import { AppSettingsService } from '@core/services/app-settings/app-settings.service';

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, RouterModule, BadgeModule, StyleClassModule],
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    host: { class: 'flex flex-col h-full' }
})
export class AppMenu {
    private menuSub!: Subscription;

    model: MenuItem[] = [];
    item!: MenuItem;

    badge = 0;

    loaded = false;

    expandedMenus: string[] = [];
    collapsedMenus: string[] = [];

    constructor(
        private menuService: MenuService,
        private appSettingsService: AppSettingsService,
        private router: Router
    ) {}

    get logoCompany(): string {
        return (
            this.appSettingsService.appearance?.companyLogoUrl ||
            'assets/images/logos/default_logo.png'
        );
    }

    get dashboardItem(): MenuItem {
        return this.model[0];
    }

    get signOutItem(): MenuItem {
        return this.model[this.model.length - 1];
    }

    get menuItems(): MenuItem[] {
        return this.model.slice(1, -1);
    }

    ngOnInit() {
        this.loadMenu();

        this.menuSub = this.menuService.menuRefresh$.subscribe(() => {
            this.loadMenu();
        });
    }
    loadMenu() {
        this.model = this.menuService.getMenu();
        this.loaded = true;
    }

    ngOnDestroy() {
        if (this.menuSub) {
            this.menuSub.unsubscribe();
        }
    }

    itemClick(event: Event, item: any) {
        if (item.disabled) {
            event.preventDefault();
            return;
        }

        if (item.command) {
            item.command({ originalEvent: event, item: item });
        }

        if (item.items) {
            const label = item.label ?? '';
            const currentlyExpanded = this.isExpanded(item);

            // Always clear both arrays first — stale entries in either array
            // cause permanent stuck state when isChildActive changes between clicks.
            this.expandedMenus = this.expandedMenus.filter((l) => l !== label);
            this.collapsedMenus = this.collapsedMenus.filter((l) => l !== label);

            if (currentlyExpanded) {
                // Collapsing. Active-child menus expand by default, so we need
                // an explicit collapsed marker to override that default.
                if (this.isChildActive(item)) {
                    this.collapsedMenus.push(label);
                }
                // Inactive menus collapse by default — removing from expandedMenus is enough.
            } else {
                // Expanding. Inactive menus collapse by default, so we need
                // an explicit expanded marker.
                if (!this.isChildActive(item)) {
                    this.expandedMenus.push(label);
                }
                // Active-child menus expand by default — removing from collapsedMenus is enough.
            }
            return;
        }

        if (window.innerWidth < 1024) {
            this.closeMobileSidebar();
        }
    }

    closeMobileSidebar(): void {
        const sidebar = document.getElementById('app-sidebar-1');
        if (sidebar) {
            sidebar.classList.add('hidden');
        }
    }

    isExpanded(item: MenuItem): boolean {
        const label = item.label ?? '';

        if (this.collapsedMenus.includes(label)) {
            return false;
        }

        return this.expandedMenus.includes(label) || this.isChildActive(item);
    }

    get groupedMenuItems(): MenuItem[] {
        return this.menuItems.filter((item) => item.items && item.items.length > 0);
    }

    get allExpanded(): boolean {
        return (
            this.groupedMenuItems.length > 0 &&
            this.groupedMenuItems.every((item) => this.isExpanded(item))
        );
    }

    expandAll(): void {
        this.collapsedMenus = [];
        this.expandedMenus = this.groupedMenuItems
            .filter((item) => !this.isChildActive(item))
            .map((item) => item.label ?? '');
    }

    collapseAll(): void {
        this.expandedMenus = [];
        this.collapsedMenus = this.groupedMenuItems
            .filter((item) => this.isChildActive(item))
            .map((item) => item.label ?? '');
    }

    private isChildActive(item: MenuItem): boolean {
        if (!item.items) return false;
        return item.items.some((child) => {
            if (!child.routerLink) return false;
            const link = Array.isArray(child.routerLink) ? child.routerLink[0] : child.routerLink;

            return this.router.isActive(link, {
                paths: 'subset',
                queryParams: 'ignored',
                fragment: 'ignored',
                matrixParams: 'ignored'
            });
        });
    }
}
